import { useEffect, useRef } from "react";
import { COPY } from "../copy.js";
import { digitsOnly, fa } from "../format.js";
import { ACCENT } from "../accent.js";
import { COLOR, css } from "../paint.js";

/**
 * The pieces both ways in are built from: the card, the code boxes, and the field styling.
 *
 * Extracted when applying to teach became its own screen (ADR-012). Two screens now ask for a phone
 * number and six digits in the same shape, and the alternative to sharing this was a second copy of
 * the one-input-under-six-boxes trick below — which is subtle enough that a copy of it would drift
 * and only the copy would be broken.
 */

/** How many digits the code has. Kept in step with the server's `OTP_DIGITS`. */
export const CODE_LENGTH = 6;

/**
 * A text field, sized for an adult filling in a form.
 *
 * **52px, not the 76px `min-h-touch` the rest of the app uses.** That token is for a five-year-old
 * pressing a game tile with a whole hand, and applying it to a five-field form made every input look
 * like a button and pushed the form off the screen — which is what it did. An adult typing their
 * national id needs a field big enough to tap accurately and no bigger; anything more is wasted height
 * that turns a short form into a scroll.
 *
 * `dir="ltr"` is on the elements, not here: numbers read left-to-right even inside an RTL page.
 */
export const FIELD =
  "h-[52px] w-full rounded-md border-2 border-edge bg-card px-md text-body outline-none transition-colors focus-visible:border-grape";

/** The action. Still generous — this one IS a button, and it is the only one on the screen. */
export const PRIMARY = `mt-lg h-[56px] w-full rounded-pill px-lg text-heading text-card ${ACCENT.grape.bg} ${ACCENT.grape.shadow}`;

export const LINK = "mt-md block min-h-11 w-full text-small text-muted";

export const LABEL = "mb-1 block text-caption text-muted";

/**
 * The code, as six boxes — drawn as boxes, typed into as ONE input.
 *
 * Six real inputs is the obvious build and it is the wrong one. It breaks `autocomplete="one-time-code"`,
 * which is how the code arrives on its own from the SMS on both iOS and Android; it breaks pasting a code
 * copied from the message; and it needs focus juggling between fields that goes wrong on Android soft
 * keyboards in ways that are hard to reproduce. So there is one transparent input stretched across the
 * whole row, and the boxes underneath are just a picture of what it contains.
 *
 * **Digits are normalised as they are typed.** The previous version filtered with `/\D/g`, which matches
 * only `0-9` — so a Persian keyboard's ۱۲۳۴۵۶ was deleted keystroke by keystroke and the field stayed
 * empty, on the one screen nobody can get past. Nothing said why.
 */
export function CodeBoxes({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const digits = Array.from({ length: CODE_LENGTH }, (_, i) => value[i] ?? "");
  // The box the next digit will land in, so exactly one is highlighted and none is once it is full.
  const active = Math.min(value.length, CODE_LENGTH - 1);

  /**
   * The field is focused on mount, and it has to be done here rather than left to `autoFocus`.
   *
   * **The boxes drew a focus ring that was a lie.** The highlighted box is computed from how many
   * digits have been typed, so the row looked ready the moment the screen appeared — while the real
   * input, invisible underneath, had never been focused. Typing did nothing and the only way through
   * was to click first, on the one screen nobody can get past.
   *
   * A ref rather than the `autoFocus` attribute: this input mounts inside a branch that swaps in after
   * a request resolves, and React applies `autoFocus` only on the initial commit of the element — which
   * in that path can land before the card is on screen. Calling `focus()` in an effect happens after the
   * node is in the document, every time.
   *
   * iOS will still not raise the keyboard without a gesture, and that is the platform's rule rather
   * than something to work around: the tap that got them here is the gesture, and the one-time-code
   * autofill fills the field without the keyboard anyway.
   */
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
  }, []);

  return (
    <div className="relative">
      {/*
        * Right to left, because the boxes fill in the direction the document runs.
        *
        * The digits themselves stay left to right inside their own box — a numeral is not a word — which
        * is what `dir="ltr"` on the input handles for the caret and selection.
        */}
      <div className="flex flex-row-reverse justify-between gap-sm" aria-hidden>
        {digits.map((digit, i) => (
          <span
            key={i}
            className={`grid h-[60px] flex-1 place-items-center rounded-md border-2 text-display transition-colors ${
              digit
                ? "border-grape bg-card text-ink"
                : i === active && value.length < CODE_LENGTH
                  ? "border-grape bg-card"
                  : "border-edge bg-cream"
            }`}
          >
            {/* Persian numerals, like every other number the product shows. */}
            {digit ? fa(Number(digit)) : ""}
          </span>
        ))}
      </div>

      {/*
        * The real field, invisible over the top.
        *
        * Its text and caret are transparent rather than the element being hidden: a hidden input cannot
        * be focused by a tap, and on iOS an off-screen one does not receive the one-time-code autofill.
        */}
      <input
        ref={input}
        id="code"
        value={value}
        onChange={(e) => onChange(digitsOnly(e.target.value).slice(0, CODE_LENGTH))}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={CODE_LENGTH}
        dir="ltr"
        aria-label={COPY.enter.codeLabel}
        className="absolute inset-0 h-full w-full rounded-md bg-transparent text-center text-transparent caret-transparent outline-none focus-visible:ring-0"
      />
    </div>
  );
}

/**
 * The card every step of both flows sits in.
 *
 * One card, one icon, and a dot per step showing where you are. Before this each step was a bare form
 * on the page, so entering a phone number and then entering a code looked like two unrelated screens
 * rather than one short errand — and somebody who cannot see how much is left assumes there is more.
 *
 * The dots are not numbered: two or three is a count you can see.
 */
export function AuthCard({
  step,
  steps = 2,
  icon = "phone",
  title,
  intro,
  children,
}: {
  step: number;
  /** How many there are in this flow: two for signing in, one for each of the endings. */
  steps?: number;
  icon?: "phone" | "code" | "form" | "done";
  title: string;
  intro?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid place-items-center py-md">
      {/*
        * 400px, with no wider variant.
        *
        * There was one, for the registration form's five fields. That form is a page of its own now
        * (`apply-screen.tsx`) and lays itself out, so the option here was a second width nothing asked
        * for — and a card component with two sizes invites a third.
        */}
      <div className="w-full max-w-[400px] rounded-xl bg-card px-lg pt-xl pb-lg shadow-raised">
        <div className="flex items-center gap-md">
          <span className="grid size-[52px] shrink-0 place-items-center rounded-pill bg-grape/12">
            <StepIcon icon={icon} />
          </span>
          {/* Hidden at one step: a single dot says nothing a person needed to be told. */}
          {steps > 1 && (
            <div className="flex gap-1.5">
              {Array.from({ length: steps }, (_, i) => i + 1).map((n) => (
                // Two states, two class strings — the width is a design choice with exactly two
                // values, so it does not need to be computed.
                <span
                  key={n}
                  className={`h-2 rounded-pill transition-[width] duration-220 ease-kd ${
                    n === step ? "w-[22px] bg-grape" : "w-2 bg-ink/14"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        <h1 className="mt-lg mb-sm text-title">{title}</h1>
        {intro && <p className="text-small text-muted">{intro}</p>}
        {children}
      </div>
    </div>
  );
}

/** A phone, an envelope, a form, or a tick. Drawn rather than lettered, so nothing needs translating. */
function StepIcon({ icon }: { icon: "phone" | "code" | "form" | "done" }) {
  const stroke = css(COLOR.grape);
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden>
      {icon === "phone" && (
        <rect x="6" y="2" width="12" height="20" rx="3" fill="none" stroke={stroke} strokeWidth="2.2" />
      )}
      {icon === "code" && (
        <>
          <rect
            x="2.5" y="5" width="19" height="14" rx="3"
            fill="none" stroke={stroke} strokeWidth="2.2"
          />
          <path
            d="M3.5 7 12 13 20.5 7"
            fill="none" stroke={stroke} strokeWidth="2.2" strokeLinecap="round"
          />
        </>
      )}
      {icon === "form" && (
        <>
          <rect
            x="4" y="2.5" width="16" height="19" rx="3"
            fill="none" stroke={stroke} strokeWidth="2.2"
          />
          <path
            d="M8 8h8M8 12h8M8 16h4"
            fill="none" stroke={stroke} strokeWidth="2.2" strokeLinecap="round"
          />
        </>
      )}
      {icon === "done" && (
        <path
          d="M4 12.5 9.5 18 20 6"
          fill="none" stroke={stroke} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
        />
      )}
    </svg>
  );
}
